// Layer 3 (Transformation): the working-curve resolver — judges a
// recovery/grade pair against the plant's seeded grade–recovery curve.
// Pure; no React, no engine mutation, no seed import beyond the type.
// The curve is an equation, never a drawing (optiflow-update.A7,
// paul-email.A4) — this file is the only place a `GradeRecoveryCurve` is
// evaluated. Never throws on a valid curve (>= 2 anchors).

import type { GradeRecoveryAnchor, GradeRecoveryCurve } from '@/versions/v1/schema/grade-recovery-curve';

export type CurveJudgement = {
  /** The grade the curve predicts for the given recovery. */
  expectedGradePct: number;
  /** Actual grade minus expected — signed. */
  deltaGradePct: number;
  /** `on` within tolerance; `above` / `below` the curve outside it. */
  position: 'on' | 'above' | 'below';
  withinTolerance: boolean;
  withinSmelterBand: boolean;
  withinTargetRecovery: boolean;
  /** The recovery fell outside the curve's own anchor range — the
   * expected grade was clamped to the nearest anchor, not interpolated. */
  extrapolated: boolean;
};

function sortedAnchors(curve: GradeRecoveryCurve): readonly GradeRecoveryAnchor[] {
  return [...curve.anchors].sort((a, b) => a.recoveryPct - b.recoveryPct);
}

/** Piecewise-linear grade at a given recovery. Clamps outside the anchor
 * range — the deterministic form the resolver evaluates (Q1). */
export function expectedGradeAt(curve: GradeRecoveryCurve, recoveryPct: number): number {
  const anchors = sortedAnchors(curve);
  const lo = anchors[0]!;
  const hi = anchors[anchors.length - 1]!;
  if (recoveryPct <= lo.recoveryPct) return lo.gradePct;
  if (recoveryPct >= hi.recoveryPct) return hi.gradePct;
  for (let i = 0; i < anchors.length - 1; i += 1) {
    const a = anchors[i]!;
    const b = anchors[i + 1]!;
    if (recoveryPct >= a.recoveryPct && recoveryPct <= b.recoveryPct) {
      const span = b.recoveryPct - a.recoveryPct;
      const t = span === 0 ? 0 : (recoveryPct - a.recoveryPct) / span;
      return a.gradePct + t * (b.gradePct - a.gradePct);
    }
  }
  return hi.gradePct;
}

/** Inverse of `expectedGradeAt` — piecewise-linear recovery at a given
 * grade. Grade falls monotonically as recovery rises, so anchors sorted by
 * recovery are also sorted descending by grade. Clamps outside range. */
export function expectedRecoveryAt(curve: GradeRecoveryCurve, gradePct: number): number {
  const anchors = sortedAnchors(curve);
  const lo = anchors[0]!;
  const hi = anchors[anchors.length - 1]!;
  if (gradePct >= lo.gradePct) return lo.recoveryPct;
  if (gradePct <= hi.gradePct) return hi.recoveryPct;
  for (let i = 0; i < anchors.length - 1; i += 1) {
    const a = anchors[i]!;
    const b = anchors[i + 1]!;
    if (gradePct <= a.gradePct && gradePct >= b.gradePct) {
      const span = a.gradePct - b.gradePct;
      const t = span === 0 ? 0 : (a.gradePct - gradePct) / span;
      return a.recoveryPct + t * (b.recoveryPct - a.recoveryPct);
    }
  }
  return hi.recoveryPct;
}

/** The judgement: is this pair on, above, or below the working curve, and
 * where does it sit against the smelter band and the target-recovery
 * range. A day's sample does not redraw the curve — it puts a point on it
 * (paul-call.A1). */
export function judgeAgainstCurve(
  curve: GradeRecoveryCurve,
  point: { recoveryPct: number; gradePct: number },
): CurveJudgement {
  const anchors = sortedAnchors(curve);
  const lo = anchors[0]!;
  const hi = anchors[anchors.length - 1]!;
  const extrapolated = point.recoveryPct < lo.recoveryPct || point.recoveryPct > hi.recoveryPct;
  const expectedGradePct = expectedGradeAt(curve, point.recoveryPct);
  const deltaGradePct = point.gradePct - expectedGradePct;
  const withinTolerance = Math.abs(deltaGradePct) <= curve.tolerance.gradePctAbs;
  const position: CurveJudgement['position'] = withinTolerance
    ? 'on'
    : deltaGradePct > 0
      ? 'above'
      : 'below';
  const withinSmelterBand =
    point.gradePct >= curve.smelterGradeBand.lo && point.gradePct <= curve.smelterGradeBand.hi;
  const withinTargetRecovery =
    point.recoveryPct >= curve.targetRecovery.lo && point.recoveryPct <= curve.targetRecovery.hi;

  return {
    expectedGradePct,
    deltaGradePct,
    position,
    withinTolerance,
    withinSmelterBand,
    withinTargetRecovery,
    extrapolated,
  };
}
